import React, { useContext, useRef, useState } from 'react';
import styles from './PassportGroup.module.scss';
import DocumentUploadArea from '../DocumentUploadArea/DocumentUploadArea';
import DocumentsGrid from '../DocumentsGrid/DocumentsGrid';
import { DEPENDENT_DOCUMENT_VISIBILITY, DEPENDENT_DOCUMENT_VISIBILITY_GLOBAL_CASE, DOCUMENT_VISIBILITY, IChecklistItem, IChecklistItemGroup, IDocument, IDocumentMetadata } from 'src/shared/types/Documents';
import { Button, Toast } from '@ds';
import trashIcon from 'src/assets/icons/trash.svg';
import {
  createPassport,
  createPassportForAllCasePassports,
  linkDocumentToChecklistItemGroup,
  uploadDocument,
} from 'src/shared/services/document-api/Documents';
import { BeneDocAdditionalContext } from 'src/shared/providers/DocumentUploadContextProvider';
import { IBeneMember } from 'src/shared/types/Beneficiary';
import { buildChecklistDocumentName, DOCUMENT_TYPE_NAME } from 'src/utils/services/documents';
import { ButtonSize } from '@ds/elements/Button/Button.types';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import {
  IPassportInfoMetaDataRequest,
  IPassportRequest,
  IPassportRequestForAllCasePassports,
} from 'src/shared/types/ImmigrationDocument';
import VerifyPassportModal from '../VerifyPassportModal/VerifyPassportModal';
import { IOption } from '@ds/elements/Select/Select.types';
import { DateFormat, getFormattedDate } from 'src/utils/formatters/formatDate';
import { CountryCode } from 'src/utils/constants/common';
import CompletePassport from './CompletePassport';

interface PassportGroupProps {
  group?: IChecklistItemGroup;
  checklistItem?: IChecklistItem;
  allCasePassports?: boolean;
  fileList?: File[];
  addFileAllCasePassports?: (file: File[]) => void;
  onRefreshChecklist?: () => void;
  setCurrentItem?: (item: IChecklistItem) => void;
  onDeletePassportGroup?: (group: IChecklistItemGroup, hideToast?: boolean) => void;
  index?: number;
  onDeleteAllCasePassportsGroup?: (index: number) => void;
  onDeleteAllCasePassportsFile?: (groupIndex: number, fileIndex: number) => void;
}

const PassportGroup: React.FC<PassportGroupProps> = ({
  group,
  checklistItem,
  allCasePassports,
  fileList,
  addFileAllCasePassports,
  onRefreshChecklist,
  setCurrentItem,
  onDeletePassportGroup,
  index,
  onDeleteAllCasePassportsGroup,
  onDeleteAllCasePassportsFile,
}) => {
  const { caseId, beneId, companyId, userId, otherDocTypeId, passportDocTypeId, caseInfo } = useContext(BeneDocAdditionalContext);
  const documents =
    group?.checklistItemDocuments?.flatMap((docItem) => (docItem.document ? [docItem.document] : [])) || [];
  const toastRef = useRef<IToastr>(null);
  const [showPassportFinishModal, setShowPassportFinishModal] = useState(false);
  const { beneMembers } = useContext(BeneDocAdditionalContext);

  const getBeneFullName = (beneMembers: IBeneMember[]): string => {
    const bene = beneMembers.find((member) => member.isBene);
    return bene?.fullName || '';
  };

  const getDependentFullName = (dependentId: number, beneMembers: IBeneMember[]): string => {
    const dependent = beneMembers.find((member) => !member.isBene && member.id === dependentId);
    return dependent?.fullName || '';
  };

  const issuedToItems: IOption[] = React.useMemo(() => {
    if (checklistItem?.beneficiary) {
      return [
        {
          label: checklistItem.beneficiary.fullName ?? '',
          value: checklistItem.beneficiaryId ?? -1,
        },
      ];
    }

    if (checklistItem?.dependent) {
      return [
        {
          label: checklistItem.dependent.dependentFullName ?? '',
          value: checklistItem.dependentId ?? -1,
        },
      ];
    }

    if (group?.passportInfo?.beneId) {
      return [
        {
          label: getBeneFullName(beneMembers) ?? '',
          value: group?.passportInfo.beneId ?? -1,
        },
      ];
    }

    if (group?.passportInfo?.dependentId) {
      return [
        {
          label: getDependentFullName(group.passportInfo.dependentId, beneMembers) ?? '',
          value: group.passportInfo.dependentId ?? -1,
        },
      ];
    }

    if (!checklistItem && beneMembers.length > 0) {
      return beneMembers.map((member) => ({
        label: member.fullName ?? '',
        value: member.id ?? -1,
      }));
    }

    return [{ key: -1, label: '', value: -1 }];
  }, [checklistItem, group?.passportInfo, beneMembers]);

  const uploadAsSingleDocument = async (
    files: File[],
    ownerName: string,
    clientCaseIds: number[],
    dependentId: number | null = null,
    overrideDocTypeId?: number
  ): Promise<IDocument[]> => {
    const docTypeId = overrideDocTypeId ? overrideDocTypeId : otherDocTypeId || 0;

    const docName = buildChecklistDocumentName({
      fileName: files[0].name,
      applicantName: ownerName,
      documentTypeName: DOCUMENT_TYPE_NAME.PASSPORT,
    });

    const visibility = dependentId
      ? (caseInfo?.caseCountryCode && caseInfo.caseCountryCode !== CountryCode.UnitedStates)
        ? DEPENDENT_DOCUMENT_VISIBILITY_GLOBAL_CASE
        : DEPENDENT_DOCUMENT_VISIBILITY
      : DOCUMENT_VISIBILITY;

    const documentMetadata: IDocumentMetadata = {
      name: docName,
      type: docTypeId,
      common: {
        visibility,
        companyId,
        clientCaseIds,
        beneId,
        dependentId,
      },
    };
    if (caseId && userId) {
      return await uploadDocument(files, documentMetadata, undefined, docName, caseId, userId);
    } else {
      return [];
    }
  };

  const handleFinishPassportAllCasePassports = async (passportMetaDataRequest: IPassportInfoMetaDataRequest) => {
    if (!fileList || fileList.length === 0 || !passportDocTypeId || !otherDocTypeId) {
      return;
    }
    const { issuedToBene, issuedTo } = passportMetaDataRequest;

    const ownerName = passportMetaDataRequest.issuedToBene
      ? getBeneFullName(beneMembers)
      : getDependentFullName(issuedTo, beneMembers);
    const dependentId = issuedToBene ? undefined : issuedTo;
    let hasError = false;
    try {
      toastRef.current?.show({ message: 'Saving...', severity: 'info', sticky: true });
      const uploadResults = (
        await Promise.all(
          fileList.map((file) =>
            uploadAsSingleDocument([file], ownerName, [caseId ?? -1], dependentId, otherDocTypeId).then((res) => {
              return res;
            })
          )
        )
      ).flat();

      const formattedPassportMetadata = {
        ...passportMetaDataRequest,
        issuedDate: passportMetaDataRequest.issuedDate
          ? getFormattedDate(passportMetaDataRequest.issuedDate, DateFormat['YYYY/MM/DD'])
          : '',
        passportExpirationDate: passportMetaDataRequest.passportExpirationDate
          ? getFormattedDate(passportMetaDataRequest.passportExpirationDate, DateFormat['YYYY/MM/DD'])
          : '',
      };

      const documentIds = uploadResults.map((result) => parseInt(result.id ?? '0'));
      const passportRequest: IPassportRequestForAllCasePassports = {
        documentIds,
        beneficiaryId: beneId ?? 0,
        dependentId,
        passportMetadata: {
          ...formattedPassportMetadata,
        },
      };
      await createPassportForAllCasePassports(passportRequest);
    } catch (e) {
      toastRef.current?.clear();
      if (
        e &&
        typeof e === 'object' &&
        'response' in e &&
        e.response &&
        typeof e.response === 'object' &&
        'data' in e.response &&
        e.response.data &&
        typeof e.response.data === 'object' &&
        'errors' in e.response.data &&
        e.response.data.errors &&
        (e.response.data.errors as { [key: string]: any })['[ValidatePassportNumber]'] != null
      ) {
        toastRef.current?.show({
          message: `This passport ${passportMetaDataRequest.passportNumber} already exists. Please link it from Cobalt.`,
          severity: 'error',
          sticky: true,
        });
      } else {
        toastRef.current?.show({
          message: 'Error saving passport information. Please try again.',
          severity: 'error',
          sticky: true,
        });
      }
      hasError = true;
    } finally {
      if (!hasError) {
        toastRef.current?.clear();
        toastRef.current?.show({ message: 'Saved', severity: 'success', sticky: false });
      }
    }
    //Tech Debt: This delay allows users to see the toast message before this parent component rerenders. To fix, move Toast to higher component level, similar to HR Web
    setTimeout(() => {
      onRefreshChecklist && onRefreshChecklist();
      onDeleteAllCasePassportsGroup && onDeleteAllCasePassportsGroup(index ?? -1);
    }, 5000);
  };

  const handlePassportFileUpload = async (fileList: File[]) => {
    if (group && checklistItem) {
      let uploadPendingFiles = fileList.length;

      let remaining = uploadPendingFiles;

      toastRef.current?.show({
        message: 'Uploading...',
        rightMessage: `${remaining} ${remaining > 1 ? 'Files' : 'File'} left`,
        severity: 'info',
        sticky: true,
      });

      const ownerName = checklistItem?.dependent
        ? checklistItem?.dependent?.dependentFullName
        : checklistItem?.beneficiary?.fullName || '';
      const dependentId = checklistItem?.dependentId || null;

      if (!group?.passportInfoId) {
        const uploadResults = (
          await Promise.all(
            fileList.map(async (file) => {
              const res: any = await uploadAsSingleDocument(
                [file],
                ownerName || '',
                [caseId ?? -1],
                dependentId,
                otherDocTypeId
              );

              remaining--;
              toastRef.current?.clear();
              toastRef.current?.show({
                message: 'Uploading...',
                rightMessage: `${remaining} ${remaining !== 1 ? 'Files' : 'File'} left`,
                severity: 'info',
                sticky: true,
              });

              return res;
            })
          )
        ).flat();

        const linkResults = await Promise.all(
          uploadResults.map((doc: IDocument) =>
            linkDocumentToChecklistItemGroup(group?.id, parseInt((doc.id || doc.docId || '0').toString()))
          )
        );

        const item: IChecklistItem = { ...checklistItem };
        item.checklistItemGroups = checklistItem?.checklistItemGroups?.slice();
        const indexExisting = checklistItem?.checklistItemGroups?.findIndex((p) => p.id == group?.id) ?? -1;

        if (indexExisting >= 0) {
          const currentGroup = checklistItem?.checklistItemGroups?.[indexExisting];
          if (currentGroup) {
            currentGroup.checklistItemDocuments = [
              ...new Map(
                [...(currentGroup.checklistItemDocuments ?? []), ...linkResults].map((doc) => [doc.documentId, doc])
              ).values(),
            ];
          }
          setCurrentItem?.(item);
        } else {
          group.checklistItemDocuments = [
            ...new Map(
              [...(group.checklistItemDocuments ?? []), ...linkResults].map((doc) => [doc.documentId, doc])
            ).values(),
          ];
        }
      }

      toastRef.current?.clear();
      toastRef.current?.show({
        message: 'Uploaded',
        rightMessage: `${uploadPendingFiles} ${uploadPendingFiles > 1 ? 'Files' : 'File'}`,
        severity: 'success',
        sticky: false,
      });

      onRefreshChecklist && onRefreshChecklist();
    }
  };

  const handleFinishPassport = async (passportMetadata: IPassportInfoMetaDataRequest) => {
    if (group && checklistItem) {
      const documentIds = group?.checklistItemDocuments?.map((result) =>
        parseInt((result.documentId ?? '0').toString())
      );

      // Convert date fields to strings
      const formattedPassportMetadata = {
        ...passportMetadata,
        issuedDate: passportMetadata.issuedDate
          ? getFormattedDate(passportMetadata.issuedDate, DateFormat['YYYY/MM/DD'])
          : '',
        passportExpirationDate: passportMetadata.passportExpirationDate
          ? getFormattedDate(passportMetadata.passportExpirationDate, DateFormat['YYYY/MM/DD'])
          : '',
      };

      const passportRequest: IPassportRequest = {
        checklistItemId: checklistItem.id,
        checklistItemGroupId: group.id,
        documentIds: documentIds || [],
        beneficiaryId: beneId ?? 0,
        dependentId: checklistItem.dependentId || null,
        passportMetadata: formattedPassportMetadata,
      };

      let hasError = false;
      try {
        toastRef.current?.show({ message: 'Saving...', severity: 'info', sticky: true });
        await createPassport(passportRequest);
      } catch (e) {
        toastRef.current?.clear();
        if (
          e &&
          typeof e === 'object' &&
          'response' in e &&
          e.response &&
          typeof e.response === 'object' &&
          'data' in e.response &&
          e.response.data &&
          typeof e.response.data === 'object' &&
          'errors' in e.response.data &&
          e.response.data.errors &&
          (e.response.data.errors as { [key: string]: any })['[ValidatePassportNumber]'] != null
        ) {
          toastRef.current?.show({
            message: `This passport ${passportMetadata.passportNumber} already exists. Please link it from Cobalt.`,
            severity: 'error',
            sticky: true,
          });
        } else {
          toastRef.current?.show({
            message: 'Error saving passport information. Please try again.',
            severity: 'error',
            sticky: true,
          });
        }
        hasError = true;
      } finally {
        if (!hasError) {
          toastRef.current?.clear();
          toastRef.current?.show({ message: 'Saved', severity: 'success', sticky: false });
        }
      }
      //Tech Debt: This delay allows users to see the toast message before this parent component rerenders. To fix, move Toast to higher component level, similar to HR Web
      setTimeout(() => {
        onRefreshChecklist && onRefreshChecklist();
      }, 5000);
    }
  };

  const documentCount = fileList?.length ?? 0;

  if (allCasePassports || !group?.passportInfo) {
    return (
      <div className={styles['initial-passport']}>
        <div className={styles['header']}>
          <p>New Passport</p>
          <div className={styles['right-header']}>
            <span>
              {allCasePassports ? documentCount : documents?.length} document
              {allCasePassports ? (documentCount === 1 ? '' : 's') : documents?.length === 1 ? '' : 's'}
            </span>
            <img
              src={trashIcon}
              className={styles['trash-icon']}
              onClick={() => {
                if (onDeletePassportGroup && group) {
                  onDeletePassportGroup(group, false);
                }
                if (onDeleteAllCasePassportsGroup && typeof index === 'number') {
                  onDeleteAllCasePassportsGroup(index);
                }
              }}
            />
          </div>
        </div>
        <div className={styles['body']}>
          {allCasePassports ? (
            <>
              <DocumentsGrid
                documents={fileList || []}
                isPassport
                onDeleteFile={(fileIdx) => {
                  if (typeof index === 'number') {
                    // Call up to parent to remove file at index
                    onDeleteAllCasePassportsFile && onDeleteAllCasePassportsFile(index, fileIdx);
                  }
                }}
              />
              <DocumentUploadArea uploadPassportFile={addFileAllCasePassports} />
            </>
          ) : (
            <>
              <DocumentsGrid
                documents={documents}
                isPassport
                currentItem={checklistItem}
                onRefetchDocuments={onRefreshChecklist}
              />
              <DocumentUploadArea uploadPassportFile={handlePassportFileUpload} />
            </>
          )}

          <div className={styles['finish-button-container']}>
            <Button
              className={styles['finish-button']}
              size={ButtonSize.large}
              onClick={() => setShowPassportFinishModal(true)}
              disabled={
                ((documents == null || documents.length === 0) && (fileList == null || fileList.length === 0)) ||
                (documents == null && fileList?.length === 0) ||
                (fileList == null && documents?.length === 0)
              }
            >
              Next: Complete uploading your passport
            </Button>
          </div>
        </div>
        <Toast ref={toastRef} />
        <VerifyPassportModal
          issuedToOptions={issuedToItems}
          isReadOnly={false}
          onFinishPassport={allCasePassports ? handleFinishPassportAllCasePassports : handleFinishPassport}
          isVisible={showPassportFinishModal}
          onClose={() => setShowPassportFinishModal(false)}
          initialFormData={{
            passportId: 0,
            country: '',
            passportNumber: '',
            issuedTo: 0,
            issuedDate: '' as unknown as Date,
            issuingAuthority: '',
            passportIssuedCountry: '',
            passportExpirationDate: '' as unknown as Date,
            issuedState: '',
            issuedCity: '',
            passportType: '',
            locationOfPassport: '',
            hasEnoughBlanks: false,
            isTemporary: false,
            isBiometric: false,
            isInactive: false,
          }}
        />
      </div>
    );
  }

  return (
    <CompletePassport
      passportInfo={group.passportInfo}
      onDeletePassportGroup={onDeletePassportGroup ? () => onDeletePassportGroup(group, true) : undefined}
      documents={documents}
    />
  );
};

export default PassportGroup;
